import { useCallback, useEffect, useRef, useState } from 'react';

const COPIED_RESET_MS = 1500;

/**
 * Clipboard write with a transient "copied" flag for button labels. `copy`
 * resolves to whether the write succeeded so the caller decides how to
 * report a failure (toast, inline text…). The reset timer is cleared on
 * unmount so a late tick never touches an unmounted component.
 */
export function useCopyToClipboard(resetMs = COPIED_RESET_MS): {
  copied: boolean;
  copy: (text: string) => Promise<boolean>;
} {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        return false;
      }
      setCopied(true);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), resetMs);
      return true;
    },
    [resetMs],
  );

  return { copied, copy };
}
